

// profile.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { 
  User, 
  Building2, 
  FileText, 
  Settings, 
  Bell, 
  LogOut,
  Loader2
} from 'lucide-react';
// At the top of your Profile component file
import { 
  getUserProfile, 
  updateUserProfile, 
  uploadAvatar, 
  deleteAvatar 
} from '../utils/loginutils';

// FormInput component
const FormInput = ({ 
  icon: Icon, 
  label, 
  id, 
  value, 
  onChange, 
  disabled, 
  placeholder, 
  type = 'text' 
}) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    <div className="mt-1 relative rounded-lg">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      </div>
      <input
        type={type}
        id={id}
        name={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg 
          focus:ring-navy-500 focus:border-navy-500 
          disabled:bg-gray-50 disabled:text-gray-500"
        placeholder={placeholder}
      />
    </div>
  </div>
);

const Profile = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [avatarLoading, setAvatarLoading] = useState(false);
  
  const [profile, setProfile] = useState({
    id: '',
    full_name: '',
    avatar_url: null,
    role: 'student',
    bio: '',
    company: '',
    position: '',
    created_at: '',
    updated_at: ''
  });

  const [formData, setFormData] = useState({
    full_name: '',
    role: '',
    bio: '',
    company: '',
    position: ''
  });

  const createInitialProfile = async (userId, userData) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .insert([
          {
            id: userId,
            full_name: userData.user_metadata?.full_name || '',
            role: 'student',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating initial profile:', err);
      throw err;
    }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const loginHistory = JSON.parse(localStorage.getItem('loginHistory') || '[]');
        const currentUser = loginHistory[0]?.userId;
        
        if (!currentUser) {
          throw new Error('No active user session found');
        }

        let userProfile;
        try {
          userProfile = await getUserProfile(currentUser);
        } catch (err) {
          const { data: { user }, error: sessionError } = await supabase.auth.getUser();
          
          if (sessionError) throw sessionError;
          
          if (user) {
            userProfile = await createInitialProfile(currentUser, user);
          } else {
            throw new Error('No user session found');
          }
        }

        setProfile(userProfile);
        setFormData({
          full_name: userProfile.full_name || '',
          role: userProfile.role || 'student',
          bio: userProfile.bio || '',
          company: userProfile.company || '',
          position: userProfile.position || ''
        });
      } catch (err) {
        setError('Failed to load profile data: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccessMessage('');

      const updatedProfile = await updateUserProfile(profile.id, {
        ...formData,
        updated_at: new Date().toISOString()
      });

      setProfile(updatedProfile);
      setSuccessMessage('Profile updated successfully!');
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.id) return;

    try {
      setAvatarLoading(true);
      setError(null);

      if (profile?.avatar_url) {
        await deleteAvatar(profile.avatar_url);
      }

      const publicUrl = await uploadAvatar(profile.id, file);

      const updatedProfile = await updateUserProfile(profile.id, {
        avatar_url: publicUrl,
        updated_at: new Date().toISOString()
      });

      setProfile(updatedProfile);
      setSuccessMessage('Profile picture updated successfully!');

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to upload avatar');
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) throw error;

      localStorage.removeItem('loginHistory');
      localStorage.removeItem('session');
      
      window.location.reload();
    } catch (err) {
      console.error('Logout error:', err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 text-navy-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-navy-600">Profile</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                className="p-2 rounded-full hover:bg-gray-100"
                aria-label="Notifications"
              >
                <Bell className="h-6 w-6 text-gray-600" />
              </button>
              <button 
                className="p-2 rounded-full hover:bg-gray-100"
                aria-label="Logout"
                onClick={handleLogout}
              >
                <LogOut className="h-6 w-6 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {successMessage}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-6">
              <div className="relative">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile.full_name || "Profile"}
                    className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/default-avatar.png';
                      setProfile(prev => ({ ...prev, avatar_url: null }));
                    }}
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full border-4 border-gray-100 bg-navy-50 flex items-center justify-center">
                    <User className="h-12 w-12 text-navy-600" />
                  </div>
                )}
                <label 
                  className="absolute bottom-0 right-0 bg-navy-600 text-white p-2 rounded-full 
                    hover:bg-navy-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-disabled={avatarLoading}
                >
                  {avatarLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    disabled={avatarLoading}
                  />
                </label>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {profile?.full_name || "Add Your Name"}
                </h2>
                <p className="text-gray-600">{profile?.role || "Add Your Role"}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                icon={User}
                label="Full Name"
                id="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="Enter your full name"
              />

              <FormInput
                icon={FileText}
                label="Role"
                id="role"
                value={formData.role}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="Enter your role"
              />

              <FormInput
                icon={Building2}
                label="Company"
                id="company"
                value={formData.company}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="Enter your company"
              />

              <FormInput
                icon={Settings}
                label="Position"
                id="position"
                value={formData.position}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="Enter your position"
              />

              <div className="md:col-span-2">
                <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                  Bio
                </label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  rows={4}
                  className="mt-1 block w-full border border-gray-300 rounded-lg 
                    focus:ring-navy-500 focus:border-navy-500 
                    disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Tell us about yourself..."
                />
              </div>
            </div>

            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-navy-600 text-white px-6 py-2 rounded-lg hover:bg-navy-700 
                  transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                  flex items-center space-x-2"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>{isSubmitting ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
